/**
 * In-app "Ping" bookkeeping for Task Management.
 *
 * Cross-user contract (manager A → employee B on different browsers):
 *   - Unread state lives on the **task row via the API** (`lastPingedAt` /
 *     `pingCount` when the backend keeps them, otherwise a `changeHistory`
 *     entry with `field: 'ping'` — same PUT fallback as edit history).
 *   - localStorage is ONLY for (1) manager-side optimistic badges and
 *     (2) per-viewer "I've already toasted this ping" seen state.
 *   - Never treat the manager's localStorage as a way to notify the
 *     employee — that cannot cross users/browsers.
 *
 * Seen-store shape (keyed by the viewing employee's empId):
 *   {
 *     byUser: {
 *       [viewerEmpId]: {
 *         tasks: { [taskId]: { seenAt, lastPingedAt } },
 *         employee: { seenAt, lastPingedAt } | null,
 *       },
 *     },
 *     // Legacy same-browser optimistic records (manager UX / badges):
 *     tasks: { [taskId]: { lastPingedAt, pingCount, managerName, ... } },
 *     employees: { [empId]: { lastPingedAt, pingCount, ... } },
 *   }
 */

import {
  taskId as getTaskId,
  taskAssigneeEmpId,
  normalizeChangeHistory,
} from './task-config'

/** localStorage key — also used by the global toast listener (`storage` events). */
export const STORAGE_KEY = 'taskModulePings.v1'

/** `changeHistory[].field` value used when dedicated ping columns are stripped. */
export const PING_HISTORY_FIELD = 'ping'

/** How long a ping badge stays "active" before aging out of the UI. */
const PING_ACTIVE_MS = 14 * 24 * 60 * 60 * 1000

function emptyStore() {
  return { tasks: {}, employees: {}, byUser: {} }
}

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    if (!parsed || typeof parsed !== 'object') return emptyStore()
    return {
      tasks: parsed.tasks && typeof parsed.tasks === 'object' ? parsed.tasks : {},
      employees: parsed.employees && typeof parsed.employees === 'object' ? parsed.employees : {},
      byUser: parsed.byUser && typeof parsed.byUser === 'object' ? parsed.byUser : {},
    }
  } catch (e) {
    return emptyStore()
  }
}

function writeStore(store) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
  } catch (e) {
    console.warn('[task-ping-store] could not persist ping state:', e)
  }
}

function isActive(record) {
  if (!record || !record.lastPingedAt) return false
  const at = Date.parse(record.lastPingedAt)
  if (Number.isNaN(at)) return false
  return (Date.now() - at) <= PING_ACTIVE_MS
}

function viewerBucket(store, viewerEmpId) {
  const key = String(viewerEmpId)
  if (!store.byUser[key] || typeof store.byUser[key] !== 'object') {
    store.byUser[key] = { tasks: {}, employee: null }
  }
  if (!store.byUser[key].tasks || typeof store.byUser[key].tasks !== 'object') {
    store.byUser[key].tasks = {}
  }
  return store.byUser[key]
}

/** True when this viewer already toasted a ping at-or-after `lastPingedAt`. */
export function isPingSeenByViewer(viewerEmpId, taskId, lastPingedAt) {
  if (viewerEmpId == null || taskId == null || !lastPingedAt) return false
  const bucket = readStore().byUser[String(viewerEmpId)]
  const seen = bucket && bucket.tasks && bucket.tasks[String(taskId)]
  if (!seen || !seen.seenAt) return false
  const seenPingAt = Date.parse(seen.lastPingedAt) || 0
  const pingAt = Date.parse(lastPingedAt) || 0
  return seenPingAt >= pingAt
}

export function isEmployeePingSeenByViewer(viewerEmpId, lastPingedAt) {
  if (viewerEmpId == null || !lastPingedAt) return false
  const bucket = readStore().byUser[String(viewerEmpId)]
  const seen = bucket && bucket.employee
  if (!seen || !seen.seenAt) return false
  const seenPingAt = Date.parse(seen.lastPingedAt) || 0
  const pingAt = Date.parse(lastPingedAt) || 0
  return seenPingAt >= pingAt
}

/**
 * Build a `changeHistory` entry that round-trips through PUT /pm/tasks/:id
 * even when dedicated `lastPingedAt` columns are unknown to the backend.
 */
export function buildPingHistoryEntry({
  managerName, managerEmpId, pingCount, lastPingedAt,
} = {}) {
  const at = lastPingedAt || new Date().toISOString()
  const count = Number(pingCount) || 1
  return {
    field: PING_HISTORY_FIELD,
    fieldLabel: 'Ping',
    oldValue: null,
    newValue: at,
    oldDisplay: '—',
    newDisplay: count > 1 ? `Asked for update (×${count})` : 'Asked for update',
    changedBy: managerEmpId != null ? managerEmpId : null,
    changedByName: managerName || 'Your manager',
    changedAt: at,
    pingCount: count,
  }
}

/**
 * Read ping metadata from a task payload returned by the API.
 * Prefers dedicated columns; falls back to the latest `changeHistory` ping entry.
 * Tolerates snake_case keys and JSON-string `changeHistory` (common API shapes).
 */
export function extractPingFromTask(task) {
  if (!task || typeof task !== 'object') return null
  const assignee = taskAssigneeEmpId(task)

  const lastPingedAtField = task.lastPingedAt || task.last_pinged_at || null
  if (lastPingedAtField) {
    const record = {
      lastPingedAt: lastPingedAtField,
      pingCount: Number(task.pingCount ?? task.ping_count) || 1,
      managerName: task.lastPingedByName || task.last_pinged_by_name
        || task.lastPingedBy || task.last_pinged_by || null,
      managerEmpId: task.lastPingedByEmpId != null
        ? task.lastPingedByEmpId
        : (task.last_pinged_by_emp_id != null ? task.last_pinged_by_emp_id : null),
      empId: assignee,
      taskTitle: task.title || null,
      seenAt: task.pingSeenAt || task.ping_seen_at || null,
      source: 'fields',
    }
    if (isActive(record)) return record
    // Stale/invalid dedicated field — still try changeHistory below.
  }

  const history = normalizeChangeHistory(task.changeHistory ?? task.change_history)
  const pingEntries = history.filter(e => e && (
    e.field === PING_HISTORY_FIELD
    || e.field === 'Ping'
    || String(e.fieldLabel || '').toLowerCase() === 'ping'
  ))
  if (!pingEntries.length) return null

  const latest = pingEntries[pingEntries.length - 1]
  const lastPingedAt = latest.changedAt || latest.changed_at || latest.newValue || latest.new_value || null
  const record = {
    lastPingedAt,
    pingCount: Number(latest.pingCount ?? latest.ping_count) || pingEntries.length || 1,
    managerName: latest.changedByName || latest.changed_by_name || 'Your manager',
    managerEmpId: latest.changedBy != null
      ? latest.changedBy
      : (latest.changed_by != null ? latest.changed_by : null),
    empId: assignee,
    taskTitle: task.title || null,
    seenAt: null,
    source: 'history',
  }
  return isActive(record) ? record : null
}

/**
 * Prefer fields/history already on the task (cross-user), then fall back to
 * the localStorage record (same-browser manager optimistic UX).
 */
export function getTaskPing(taskOrId) {
  const id = typeof taskOrId === 'object' && taskOrId != null
    ? getTaskId(taskOrId)
    : taskOrId
  if (id == null) return null

  const fromTask = typeof taskOrId === 'object' && taskOrId != null
    ? extractPingFromTask(taskOrId)
    : null

  const store = readStore()
  const fromStore = store.tasks[String(id)] || null
  const storeActive = isActive(fromStore) ? fromStore : null

  if (!fromTask && !storeActive) return null

  if (fromTask && storeActive) {
    const taskAt = Date.parse(fromTask.lastPingedAt) || 0
    const storeAt = Date.parse(storeActive.lastPingedAt) || 0
    const newer = taskAt >= storeAt ? fromTask : storeActive
    return {
      ...storeActive,
      ...fromTask,
      lastPingedAt: newer.lastPingedAt,
      pingCount: Math.max(Number(fromTask.pingCount) || 0, Number(storeActive.pingCount) || 0) || 1,
      managerName: newer.managerName || fromTask.managerName || storeActive.managerName,
      managerEmpId: newer.managerEmpId != null ? newer.managerEmpId : (fromTask.managerEmpId || storeActive.managerEmpId),
      empId: fromTask.empId != null ? fromTask.empId : storeActive.empId,
      taskTitle: fromTask.taskTitle || storeActive.taskTitle || null,
      seenAt: fromTask.seenAt || storeActive.seenAt || null,
    }
  }
  return fromTask || storeActive
}

export function getEmployeePing(empId) {
  if (empId == null) return null
  const record = readStore().employees[String(empId)]
  return isActive(record) ? record : null
}

/** True when this task has an active in-app ping (badge should show). */
export function isTaskPinged(task) {
  return !!getTaskPing(task)
}

export function recordTaskPing({
  taskId, empId, managerName, managerEmpId, taskTitle,
} = {}) {
  if (taskId == null) return null
  const store = readStore()
  const key = String(taskId)
  const prev = store.tasks[key] || {}
  const record = {
    lastPingedAt: new Date().toISOString(),
    pingCount: (Number(prev.pingCount) || 0) + 1,
    managerName: managerName || prev.managerName || 'Your manager',
    managerEmpId: managerEmpId != null ? managerEmpId : (prev.managerEmpId || null),
    empId: empId != null ? empId : (prev.empId || null),
    taskTitle: taskTitle || prev.taskTitle || null,
    seenAt: null,
  }
  store.tasks[key] = record
  writeStore(store)
  return record
}

export function recordEmployeePing({ empId, managerName, managerEmpId } = {}) {
  if (empId == null) return null
  const store = readStore()
  const key = String(empId)
  const prev = store.employees[key] || {}
  const record = {
    lastPingedAt: new Date().toISOString(),
    pingCount: (Number(prev.pingCount) || 0) + 1,
    managerName: managerName || prev.managerName || 'Your manager',
    managerEmpId: managerEmpId != null ? managerEmpId : (prev.managerEmpId || null),
    seenAt: null,
  }
  store.employees[key] = record
  writeStore(store)
  return record
}

/**
 * Mark a task ping as toast-shown for this viewer. Seen is keyed by
 * `viewerEmpId` so manager/employee state never collide, and by
 * `lastPingedAt` so a later re-ping can toast again.
 */
export function markTaskPingSeen(taskId, extras = {}, viewerEmpId = null) {
  if (taskId == null) return
  const store = readStore()
  const lastPingedAt = extras.lastPingedAt || new Date().toISOString()

  // Legacy row (manager badges / same-browser) — keep in sync.
  const key = String(taskId)
  const prev = store.tasks[key] || {}
  store.tasks[key] = {
    lastPingedAt: prev.lastPingedAt || lastPingedAt,
    pingCount: Number(prev.pingCount) || Number(extras.pingCount) || 1,
    managerName: prev.managerName || extras.managerName || null,
    managerEmpId: prev.managerEmpId != null ? prev.managerEmpId : (extras.managerEmpId || null),
    empId: prev.empId != null ? prev.empId : (extras.empId != null ? extras.empId : null),
    taskTitle: prev.taskTitle || extras.taskTitle || null,
    seenAt: new Date().toISOString(),
  }

  const viewer = viewerEmpId != null ? viewerEmpId : extras.viewerEmpId
  if (viewer != null) {
    const bucket = viewerBucket(store, viewer)
    bucket.tasks[key] = {
      seenAt: new Date().toISOString(),
      lastPingedAt,
    }
  }

  writeStore(store)
}

export function markEmployeePingSeen(empId, extras = {}) {
  if (empId == null) return
  const store = readStore()
  const key = String(empId)
  const prev = store.employees[key]
  const lastPingedAt = (prev && prev.lastPingedAt) || extras.lastPingedAt || new Date().toISOString()
  if (prev) {
    store.employees[key] = { ...prev, seenAt: new Date().toISOString() }
  }
  const bucket = viewerBucket(store, empId)
  bucket.employee = {
    seenAt: new Date().toISOString(),
    lastPingedAt,
  }
  writeStore(store)
}

/**
 * Overlay localStorage ping metadata onto a freshly-fetched task list so
 * manager badges still render before/without API round-trip of ping fields.
 * Does not overwrite a newer `lastPingedAt` already on the task (fields or history).
 */
export function mergePingsOntoTasks(tasks) {
  if (!Array.isArray(tasks) || !tasks.length) return tasks || []
  const store = readStore()
  return tasks.map(task => {
    const id = getTaskId(task)
    if (id == null) return task
    const fromServer = extractPingFromTask(task)
    const record = store.tasks[String(id)]
    if (!record || !isActive(record)) return task
    const serverAt = fromServer ? (Date.parse(fromServer.lastPingedAt) || 0) : 0
    const storeAt = Date.parse(record.lastPingedAt) || 0
    if (serverAt >= storeAt && fromServer) return task
    return {
      ...task,
      lastPingedAt: record.lastPingedAt,
      pingCount: record.pingCount,
      lastPingedByName: record.managerName,
      lastPingedByEmpId: record.managerEmpId,
      pingSeenAt: record.seenAt || task.pingSeenAt || null,
    }
  })
}

/**
 * Unread in-app pings targeting this assignee — used by the global sticky
 * toast (App.vue). **Primary source is `tasks` from the API** (so a ping
 * written by the manager's browser reaches the employee). localStorage
 * task rows are only a same-browser supplement.
 *
 * Assignee identity matches Task board "mine" filtering: `assignedTo`
 * (`taskAssigneeEmpId`), compared as strings.
 */
export function getUnreadPingsForAssignee(empId, tasks = []) {
  if (empId == null) return { taskPings: [], employeePing: null }
  const empKey = String(empId)
  const seenIds = new Set()
  const taskPings = []

  ;(tasks || []).forEach(task => {
    const assignee = taskAssigneeEmpId(task)
    if (assignee == null || String(assignee) !== empKey) return
    const ping = getTaskPing(task)
    if (!ping || !ping.lastPingedAt) return
    if (isPingSeenByViewer(empId, getTaskId(task), ping.lastPingedAt)) return
    // Legacy seenAt on the merged record (same-browser store).
    if (ping.seenAt) {
      const seenAt = Date.parse(ping.seenAt) || 0
      const pingAt = Date.parse(ping.lastPingedAt) || 0
      if (seenAt >= pingAt) return
    }
    const id = getTaskId(task)
    if (id != null) seenIds.add(String(id))
    taskPings.push({ task, ping: { ...ping, empId: empKey } })
  })

  const store = readStore()
  Object.keys(store.tasks || {}).forEach(id => {
    if (seenIds.has(id)) return
    const record = store.tasks[id]
    if (!isActive(record)) return
    if (String(record.empId) !== empKey) return
    if (isPingSeenByViewer(empId, id, record.lastPingedAt)) return
    if (record.seenAt) {
      const seenAt = Date.parse(record.seenAt) || 0
      const pingAt = Date.parse(record.lastPingedAt) || 0
      if (seenAt >= pingAt) return
    }
    taskPings.push({
      task: {
        id,
        title: record.taskTitle || 'a task',
        assignedTo: record.empId,
        lastPingedAt: record.lastPingedAt,
        pingCount: record.pingCount,
        lastPingedByName: record.managerName,
      },
      ping: record,
    })
  })

  const employeePing = getEmployeePing(empId)
  const unreadEmployee = employeePing
    && !isEmployeePingSeenByViewer(empId, employeePing.lastPingedAt)
    && !employeePing.seenAt
    ? employeePing
    : null
  return { taskPings, employeePing: unreadEmployee }
}

/**
 * Whether a PUT response still carries ping data the employee can read.
 * Used by the manager path to warn when the API strips both dedicated
 * fields and the `changeHistory` fallback.
 */
export function responseRetainsPing(savedTask, expectedAt) {
  if (!savedTask || typeof savedTask !== 'object') return false
  const extracted = extractPingFromTask(savedTask)
  if (!extracted) return false
  if (!expectedAt) return true
  // Allow small clock skew; presence of any active ping is enough.
  return !!extracted.lastPingedAt
}

export default {
  STORAGE_KEY,
  PING_HISTORY_FIELD,
  getTaskPing,
  getEmployeePing,
  isTaskPinged,
  recordTaskPing,
  recordEmployeePing,
  markTaskPingSeen,
  markEmployeePingSeen,
  mergePingsOntoTasks,
  getUnreadPingsForAssignee,
  extractPingFromTask,
  buildPingHistoryEntry,
  responseRetainsPing,
  isPingSeenByViewer,
  isEmployeePingSeenByViewer,
}
